import { useState } from "react";
import { X, Send, Loader2, PenLine } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";

/* "Share your {City} moment" composer used inside CityGuideRow.
   Signed-in travellers post a short story from Home; guests see a sign-in nudge. */
export default function StoryComposer({ city, onPosted, testid = "story-composer" }) {
  const { user } = useAuth() || {};
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const signedIn = !!user;

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || busy) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/city/${encodeURIComponent(city)}/stories`, {
        title: title.trim(),
        text: text.trim(),
        author: user?.name || user?.email || "Traveller",
      });
      setTitle(""); setText(""); setOpen(false);
      onPosted?.(data);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Couldn't post your story — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={`${testid}-open`}
        className="w-full min-h-[220px] rounded-2xl border-2 border-dashed border-blue-300 bg-blue-50/40 hover:bg-blue-50 hover:border-blue-400 transition-all p-5 flex flex-col items-center justify-center text-center gap-3">
        <span className="w-12 h-12 rounded-2xl bg-blue-600 text-white grid place-items-center shadow-sm">
          <PenLine className="w-5 h-5" />
        </span>
        <div>
          <div className="font-serif font-800 text-lg text-blue-900">Share your {city} moment</div>
          <div className="text-xs text-slate-600 mt-1">Tip, photo caption or a quick note — 30 seconds and you're done</div>
        </div>
        <span className="text-xs font-700 text-blue-700 mt-1">
          {signedIn ? `Post as ${user?.name || "you"} →` : "Sign in to post →"}
        </span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] grid place-items-end sm:place-items-center bg-slate-900/50 backdrop-blur-sm p-4"
          onClick={() => setOpen(false)} data-testid={`${testid}-modal`}>
          <div className="w-full max-w-md bg-white rounded-3xl border border-slate-200 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
              <div>
                <div className="font-serif font-800 text-lg text-blue-900">Share a {city} story</div>
                <div className="text-xs text-slate-500">Your note appears in the Latest {city} Stories strip</div>
              </div>
              <button onClick={() => setOpen(false)} data-testid={`${testid}-close`}
                className="w-8 h-8 rounded-full hover:bg-slate-100 grid place-items-center text-slate-500"><X className="w-4 h-4" /></button>
            </div>

            {!signedIn ? (
              <div className="p-5 text-center space-y-3">
                <div className="text-sm text-slate-700">Sign in to share your travel notes with the community.</div>
                <a href="/login" data-testid={`${testid}-signin`}
                  className="inline-flex px-5 py-2.5 rounded-full bg-blue-600 text-white font-700 hover:brightness-110">Sign in</a>
              </div>
            ) : (
              <form onSubmit={submit} className="p-5 space-y-3">
                <div>
                  <label className="text-xs font-700 text-slate-500 uppercase tracking-wider">Title</label>
                  <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={140}
                    data-testid={`${testid}-title`}
                    placeholder={`A perfect afternoon in ${city}`}
                    className="mt-1.5 w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 outline-none focus:border-blue-500 text-slate-800" />
                </div>
                <div>
                  <label className="text-xs font-700 text-slate-500 uppercase tracking-wider">Note (optional)</label>
                  <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={1200} rows={4}
                    data-testid={`${testid}-text`}
                    placeholder="A tip, a moment, a favourite spot..."
                    className="mt-1.5 w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 outline-none focus:border-blue-500 text-slate-800 resize-none" />
                  <div className="text-[10px] text-slate-400 text-right mt-1">{text.length} / 1200</div>
                </div>
                {err && <div className="text-xs text-rose-600 bg-rose-50 rounded-lg px-3 py-2">{err}</div>}
                <button type="submit" disabled={!title.trim() || busy} data-testid={`${testid}-submit`}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 text-white py-3 font-700 hover:brightness-110 disabled:opacity-50">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {busy ? "Posting…" : "Post my story"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
